import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client-wrapper";
import { useAuth } from "./useAuth";

export interface SubscriptionState {
  tier: "starter" | "professional" | "business" | "free" | null;
  tierName: string;
  isSubscribed: boolean;
  isTrial: boolean;
  trialEndsAt: Date | null;
  subscriptionEnd: Date | null;
  loading: boolean;
  status?: "active" | "trialing" | "pending" | null;
}

export interface TierFeatures {
  teamMembers: number;
  aiConsultant: boolean;
  advancedReports: boolean;
  apiAccess: boolean;
  whiteLabel: boolean;
  prioritySupport: boolean;
}

const TIER_FEATURES: Record<string, TierFeatures> = {
  starter: {
    teamMembers: 3,
    aiConsultant: false,
    advancedReports: false,
    apiAccess: false,
    whiteLabel: false,
    prioritySupport: false,
  },
  professional: {
    teamMembers: 10,
    aiConsultant: true,
    advancedReports: true,
    apiAccess: false,
    whiteLabel: false,
    prioritySupport: false,
  },
  business: {
    teamMembers: -1, // unlimited
    aiConsultant: true,
    advancedReports: true,
    apiAccess: true,
    whiteLabel: true,
    prioritySupport: true,
  },
  free: {
    teamMembers: 1,
    aiConsultant: false,
    advancedReports: false,
    apiAccess: false,
    whiteLabel: false,
    prioritySupport: false,
  },
};

const defaultState: SubscriptionState = {
  tier: null,
  tierName: "Free Beta",
  isSubscribed: false,
  isTrial: true,
  trialEndsAt: null,
  subscriptionEnd: null,
  loading: true,
  status: null,
};

const SubscriptionContext = createContext<{
  subscription: SubscriptionState;
  features: TierFeatures;
  hasFeature: (feature: keyof TierFeatures) => boolean;
  canAddTeamMember: (currentCount: number) => boolean;
  refresh: () => Promise<void>;
  startCheckout: (priceId: string, discountCode?: string) => Promise<void>;
  activateFreeSubscription: (tierId: string, discountCode: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}>({
  subscription: defaultState,
  features: TIER_FEATURES.free,
  hasFeature: () => false,
  canAddTeamMember: () => false,
  refresh: async () => {},
  startCheckout: async () => {},
  activateFreeSubscription: async () => {},
  openCustomerPortal: async () => {},
});

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionState>(defaultState);

  const loadSubscription = useCallback(async () => {
    if (!user) {
      setSubscription({ ...defaultState, loading: false });
      return;
    }

    try {
      // First, check real Stripe subscription status
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke("check-subscription");
      
      if (!stripeError && stripeData?.subscribed) {
        // User has an active Stripe subscription
        const tierId = stripeData.tier as "starter" | "professional" | "business";
        setSubscription({
          tier: tierId,
          tierName: stripeData.tierName,
          isSubscribed: true,
          isTrial: stripeData.status === "trialing",
          trialEndsAt: null,
          subscriptionEnd: stripeData.subscriptionEnd ? new Date(stripeData.subscriptionEnd) : null,
          loading: false,
          status: stripeData.status,
        });
        return;
      }

      // Fallback: Check for pending subscription in database (trial/beta users)
      const { data: pendingSub } = await supabase
        .from("pending_subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (pendingSub) {
        const tierId = pendingSub.tier_id as "starter" | "professional" | "business";
        // Calculate trial end (14 days from selection)
        const trialEnd = new Date(pendingSub.selected_at);
        trialEnd.setDate(trialEnd.getDate() + 14);

        setSubscription({
          tier: tierId,
          tierName: pendingSub.tier_name,
          isSubscribed: false,
          isTrial: true,
          trialEndsAt: trialEnd,
          subscriptionEnd: null,
          loading: false,
          status: "pending",
        });
        return;
      }

      // Check localStorage for selected tier (backup for users who selected but haven't signed up yet)
      const storedTier = localStorage.getItem("selectedTier");
      if (storedTier) {
        try {
          const tier = JSON.parse(storedTier);
          const trialEnd = new Date(tier.selectedAt);
          trialEnd.setDate(trialEnd.getDate() + 14);

          setSubscription({
            tier: tier.id as "starter" | "professional" | "business",
            tierName: tier.name,
            isSubscribed: false,
            isTrial: true,
            trialEndsAt: trialEnd,
            subscriptionEnd: null,
            loading: false,
            status: "pending",
          });
          return;
        } catch (e) {
          // Invalid stored tier
        }
      }

      // Default to free beta
      setSubscription({
        tier: "free",
        tierName: "Free Beta",
        isSubscribed: false,
        isTrial: true,
        trialEndsAt: null,
        subscriptionEnd: null,
        loading: false,
        status: null,
      });
    } catch (error) {
      console.error("Error loading subscription:", error);
      setSubscription({
        ...defaultState,
        tier: "free",
        tierName: "Free Beta",
        loading: false,
      });
    }
  }, [user]);

  // Start Stripe checkout
  const startCheckout = useCallback(async (priceId: string, discountCode?: string) => {
    if (!user) {
      throw new Error("Must be logged in to start checkout");
    }

    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { priceId, discountCode },
    });

    if (error) {
      throw new Error(error.message || "Failed to create checkout session");
    }

    if (data?.url) {
      window.open(data.url, "_blank");
    } else {
      throw new Error("No checkout URL returned");
    }
  }, [user]);

  // Activate free subscription with discount code
  const activateFreeSubscription = useCallback(async (tierId: string, discountCode: string) => {
    if (!user) {
      throw new Error("Must be logged in to activate free subscription");
    }

    const { data, error } = await supabase.functions.invoke("activate-free-subscription", {
      body: { tierId, discountCode },
    });

    if (error) {
      throw new Error(error.message || "Failed to activate free subscription");
    }

    if (data?.success) {
      // Refresh subscription status
      await loadSubscription();
    } else {
      throw new Error(data?.error || "Failed to activate");
    }
  }, [user, loadSubscription]);

  // Open Stripe customer portal
  const openCustomerPortal = useCallback(async () => {
    if (!user) {
      throw new Error("Must be logged in to access customer portal");
    }

    const { data, error } = await supabase.functions.invoke("customer-portal");

    if (error) {
      throw new Error(error.message || "Failed to open customer portal");
    }

    if (data?.url) {
      window.open(data.url, "_blank");
    } else {
      throw new Error("No portal URL returned");
    }
  }, [user]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Auto-refresh subscription status periodically (every 60 seconds)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      loadSubscription();
    }, 60000);

    return () => clearInterval(interval);
  }, [user, loadSubscription]);

  const features = TIER_FEATURES[subscription.tier || "free"];

  const hasFeature = (feature: keyof TierFeatures): boolean => {
    return !!features[feature];
  };

  const canAddTeamMember = (currentCount: number): boolean => {
    if (features.teamMembers === -1) return true; // unlimited
    return currentCount < features.teamMembers;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        features,
        hasFeature,
        canAddTeamMember,
        refresh: loadSubscription,
        startCheckout,
        activateFreeSubscription,
        openCustomerPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
